import { Check } from "lucide-react";
import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

type BookingProgressProps = {
  steps: Array<string | { title?: string; label?: string; id?: string }>;
  currentStep?: number;
  currentStepIndex?: number;
  maxStep?: number;
  onStepClick?: (step: number) => void;
};

export function BookingProgress({ steps, currentStep, currentStepIndex, maxStep, onStepClick }: BookingProgressProps) {
  const activeStep = currentStep ?? currentStepIndex ?? 0;
  const lastEnabledStep = maxStep ?? activeStep;
  const stepGridStyle = { "--step-count": steps.length } as CSSProperties;
  const activeLabel =
    typeof steps[activeStep] === "string"
      ? steps[activeStep]
      : (steps[activeStep]?.title ?? steps[activeStep]?.label ?? steps[activeStep]?.id ?? `Step ${activeStep + 1}`);

  return (
    <div className="rounded-lg border-2 border-accent/10 bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3 sm:hidden">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase text-muted-foreground">Step {activeStep + 1} of {steps.length}</p>
          <p className="truncate text-base font-black text-accent">{activeLabel}</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-extrabold text-secondary-foreground">
          {Math.round(((activeStep + 1) / steps.length) * 100)}%
        </span>
      </div>
      <div className="mb-3 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }} />
      </div>
      <details className="sm:hidden">
        <summary className="cursor-pointer rounded-md bg-secondary/70 px-3 py-2 text-sm font-extrabold text-accent">
          View booking steps
        </summary>
        <ol className="mt-2 grid gap-2">
          {steps.map((step, index) => {
            const label = typeof step === "string" ? step : (step.title ?? step.label ?? step.id ?? `Step ${index + 1}`);
            const isComplete = index < activeStep;
            const isCurrent = index === activeStep;
            const isEnabled = index <= lastEnabledStep;
            return (
              <li key={label}>
                <button
                  type="button"
                  disabled={!isEnabled}
                  onClick={() => onStepClick?.(index)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-bold transition-colors",
                    isCurrent && "bg-accent text-accent-foreground",
                    !isCurrent && isEnabled && "bg-secondary/70 text-accent hover:bg-secondary",
                    !isEnabled && "cursor-not-allowed bg-muted/60 text-muted-foreground",
                  )}
                >
                  <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[11px] text-accent", isComplete && "text-primary", isCurrent && "text-accent")}>
                    {isComplete ? <Check className="h-3.5 w-3.5" /> : index + 1}
                  </span>
                  <span className="truncate">{label}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </details>
      <ol className="hidden gap-2 sm:grid sm:grid-cols-3 lg:grid-cols-[repeat(var(--step-count),minmax(0,1fr))]" style={stepGridStyle}>
        {steps.map((step, index) => {
          const label = typeof step === "string" ? step : (step.title ?? step.label ?? step.id ?? `Step ${index + 1}`);
          const isComplete = index < activeStep;
          const isCurrent = index === activeStep;
          const isEnabled = index <= lastEnabledStep;
          return (
            <li key={label}>
              <button
                type="button"
                disabled={!isEnabled}
                onClick={() => onStepClick?.(index)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-bold transition-colors",
                  isCurrent && "bg-accent text-accent-foreground",
                  !isCurrent && isEnabled && "bg-secondary/70 text-accent hover:bg-secondary",
                  !isEnabled && "cursor-not-allowed bg-muted/60 text-muted-foreground",
                )}
              >
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[11px] text-accent", isComplete && "text-primary", isCurrent && "text-accent")}>
                  {isComplete ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                <span className="truncate">{label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
